import { ISearchParams, SearchResponse } from "@common/models";
import { http } from "@common/utils";
import { trackPromise } from "react-promise-tracker";
import { ServiceHelper } from "./serviceHelper";
import { ServiceMethodOptions } from "./types";

export class SearchService {
    private static baseUrl: string = '/api/';

    public static search = <
        TSearchResultModelInterface,
        TSearchResultModelClass extends Record<keyof TSearchResultModelInterface, any>,
        TSearchResult
    >(
        endPoint: SearchService.SearchEndPoints,
        searchParams: ISearchParams,
        mapResults: (result: TSearchResult) => SearchResponse<TSearchResultModelClass>,
        options?: ServiceMethodOptions
    ): Promise<SearchResponse<TSearchResultModelClass>> => {
        const promise = new Promise<SearchResponse<TSearchResultModelClass>>((resolve, reject) => {
            http(`${this.baseUrl}${endPoint}`).post<TSearchResult>(searchParams).then((data) => {
                const searchResults = mapResults(data);
                ServiceHelper.showSuccess("Search Successful", options);
                resolve(searchResults);
            }).catch((error) => {
                ServiceHelper.showError(`Unable to search stores; ${error.message}`, options);
                reject(error);
            });
        });
        return trackPromise(promise, options?.promiseArea ?? SearchService.getSearchTag(endPoint));
    }

}

export namespace SearchService {
    export enum SearchEndPoints {
        // Keep in alphabetical order
        additionalInfoLabel = 'search/additionalinfolabels',
        assessmentReferrals = 'search/assessmentreferrals',
        appointmentTypes = 'search/appointmentTypes',
        breeds = 'search/breeds',
        callCodes = 'search/callcodes',
        callOrigins = 'search/callorigins',
        clientcommunications = 'search/clientcommunications',
        clients = 'searchclients',
        clientsrecentvisits = 'searchclientsrecentvisits',
        clinicIncentives = 'search/clinicincentives',
        clinictasktypes = 'search/clinictasktypes',
        clinicroles = 'search/clinicroles',
        clinicincentiveperiods = 'search/clinicincentiveperiods',
        diagnosis = 'search/diagnosis',
        duplicateClients = 'searchduplicateclients',
        examcategories = 'search/examcategories',
        featureflags = 'search/featureflags',
        hospitals = 'search/hospitals',
        hospitalCommunications = 'search/hospitalcommunications',
        hospitalOrganizations = 'search/hospitalorganizations',
        hospitalScheduleTemplates = 'search/hospitalScheduleTemplates',
        hospitalPrescriptionRequests = 'search/hospitalPrescriptionRequests',
        invoices = 'search/invoices',
        invoiceSummaries = 'search/invoiceSummaries',
        macros = 'search/macros',
        labOrders = 'search/labOrders',
        markets = 'search/markets',
        openappointments = 'search/openappointments',
        organizations = 'search/organizations',
        organizationUsers = 'search/organizationusers',
        salesitem = 'search/salesitem',
        salesitemoptions = 'search/salesitemoptions',
        salespackages = 'search/salespackages',
        salesplans = 'search/salesplan',
        petAlerts = 'search/petalerts',
        petcolors = 'search/petcolors',
        petNotPresentVisits = 'search/petnotpresentvisits',
        prescriptionlabels = 'search/prescriptionlabels',
        rabiesInformations = 'search/rabiesinformations',
        referralTypes = 'search/referraltypes',
        reportinggroups = 'search/reportinggroups',
        restrictedwords = 'search/restrictedwords',
        salesCategories = 'search/salescategories',
        salesitemresetcategories = 'search/salesitemresetcategories',
        salesrequirements = 'search/salesrequirements',
        sections = 'search/sections',
        states = 'search/states',
        stores = 'search/stores',
        tasks = 'search/tasks',
        tasksCount = 'search/taskscount',
        templates = 'search/templates',
        trustedApplications = 'search/trustedapplications',
        users = 'search/users',
        vaccineAdministereds = 'search/vaccineAdministereds',
        vaccineManufacturers = 'search/vaccineManufacturers',
        vaccineNames = 'search/vaccineNames',
        vaccineVirusTypes = 'search/vaccinevirustypes',
        zipcodes = 'search/zipcodes',
        // Keep in alphabetical order
    }



    export const getSearchTag = (endPoint: SearchEndPoints) => {
        switch (endPoint) {
            case SearchEndPoints.additionalInfoLabel:
                return 'searchAdditionalInfoLabels';
            case SearchEndPoints.appointmentTypes:
                return 'searchAppointmentTypes';
            case SearchEndPoints.assessmentReferrals:
                return 'searchAssessmentReferrals';
            case SearchEndPoints.breeds:
                return 'searchBreeds';
            case SearchEndPoints.clientcommunications:
                return 'searchClientCommunications';
            case SearchEndPoints.clients:
                return 'searchClients';
            case SearchEndPoints.clientsrecentvisits:
                return 'searchClientsRecentVisits';
            case SearchEndPoints.callCodes:
                return 'searchCallCodes';
            case SearchEndPoints.callOrigins:
                return 'searchCallOrigins';
            case SearchEndPoints.clinicIncentives:
                return 'searchClinicIncentives';
            case SearchEndPoints.clinicincentiveperiods:
                return 'searchClinicIncentivePeriods';
            case SearchEndPoints.clinicroles:
                return 'searchClinicRoles';
            case SearchEndPoints.clinictasktypes:
                return 'searchClinicTaskTypes';
            case SearchEndPoints.diagnosis:
                return 'diagnosis';
            case SearchEndPoints.duplicateClients:
                return 'searchDuplicateClients';
            case SearchEndPoints.examcategories:
                return 'searchExamCategories';
            case SearchEndPoints.featureflags:
                return 'searchFeatureFlags';
            case SearchEndPoints.hospitalCommunications:
                return "searchHospitalCommunications";
            case SearchEndPoints.hospitalScheduleTemplates:
                return "searchHospitalSchduleTemplates";
            case SearchEndPoints.hospitalPrescriptionRequests:
                return "hospitalPrescriptionRequests";
            case SearchEndPoints.hospitalOrganizations:
                return "hospitalOrganizations";
            case SearchEndPoints.hospitals:
                return "searchHospitals";
            case SearchEndPoints.invoices:
                return "searchInvoices";
            case SearchEndPoints.invoiceSummaries:
                return "searchInvoiceSummaries";
            case SearchEndPoints.labOrders:
                return "searchLabOrders";
            case SearchEndPoints.markets:
                return 'searchMarkets';
            case SearchEndPoints.macros:
                return 'searchMacros';
            case SearchEndPoints.openappointments:
                return 'openappointments'
            case SearchEndPoints.organizations:
                return "searchOrganizations";
            case SearchEndPoints.organizationUsers:
                return "searchUsers";
            case SearchEndPoints.salespackages:
                return "searchSalesPackages";
            case SearchEndPoints.salesplans:
                return "searchSalesPlans";
            case SearchEndPoints.salesitem:
                return "searchSalesItem";
            case SearchEndPoints.salesitemoptions:
                return "searchSalesitemoptions";
            case SearchEndPoints.petAlerts:
                return 'searchPetAlerts';
            case SearchEndPoints.petcolors:
                return 'searchPetColors';
            case SearchEndPoints.petNotPresentVisits:
                return 'petNotPresentVisits';
            case SearchEndPoints.prescriptionlabels:
                return "searchPrescriptionLabels";
            case SearchEndPoints.referralTypes:
                return "searchReferralTypes";
            case SearchEndPoints.salesCategories:
                return "searchSalesCategories";
            case SearchEndPoints.salesitemresetcategories:
                return "searchSalesitemresetcategories";
            case SearchEndPoints.salesrequirements:
                return "searchSalesrequirements";
            case SearchEndPoints.sections:
                return "searchSections";
            case SearchEndPoints.states:
                return "searchStates";
            case SearchEndPoints.stores:
                return 'searchStores';
            case SearchEndPoints.reportinggroups:
                return "searchReportinggroups";
            case SearchEndPoints.tasks:
                return "searchTasks";
            case SearchEndPoints.tasksCount:
                return "searchTasksCount";
            case SearchEndPoints.templates:
                return "searchTemplates";
            case SearchEndPoints.trustedApplications:
                return "searchTrustedApplications";
            case SearchEndPoints.users:
                return "searchUsers";
            case SearchEndPoints.vaccineAdministereds:
                return "vaccineAdministereds";
            case SearchEndPoints.vaccineManufacturers:
                return "vaccineManufacturers";
            case SearchEndPoints.vaccineNames:
                return "vaccineNames";
            case SearchEndPoints.vaccineVirusTypes:
                return "searchVaccineVirusTypes";
            case SearchEndPoints.zipcodes:
                return "searchZipCodes";
            case SearchEndPoints.rabiesInformations:
                return "rabiesInformations";
            case SearchEndPoints.restrictedwords:
                return "restrictedWords";
            default:
                throw 'Cannot find search tag for endpoint. If this is a new search endpoint you need to add a case to searchService.getSearchTag.';
        }
    }
}
